// Compiled, integer-only game definitions (IMPLEMENTATION §0.1, §2.1). Produced by data/compile.ts from
// shared/data/*.json and written to shared/compiled/simdefs.json by `npm run schema`; Unity loads the same
// file and never recomputes a formula. Every number is a safe integer: ticks (20 Hz), milli (damage,
// hardness), permille (multipliers, odds, fractions) or plain integers (money, counts, tiers).
// v0.2 content stays in the defs, tagged with `version`; `isActive` filters it against `contentVersion`.
import type { Int } from './core/types.ts';
import {
  type BType,
  type EffectId,
  type ExpansionId,
  type FamId,
  type Kind,
  type Mat,
  type OreId,
  type RecipeId,
  type ShopId,
  type StallId,
  type TipId,
  type UnlockId,
  VERSIONS,
  type Version,
} from './gen/ids.ts';

export interface SimDefs {
  /** simdefs.json format revision (bumped when this interface changes shape). */
  format: Int;
  /** The content version this build ships; entries with a later `version` are inactive. */
  contentVersion: Version;
  tickHz: Int;
  /** Units in one item of tier t, index t-1 (2^(t-1)). */
  tierUnits: Int[];
  materials: MaterialDef[];
  families: FamilyDef[];
  /** Gun stats by tier (index t-1). */
  guns: GunTierDef[];
  deposits: DepositDef[];
  depositRules: DepositRules;
  machineRules: MachineRules;
  buildings: BuildingDefs;
  recipes: RecipeDef[];
  /**
   * Everything with a price, in shop order. `prices[n]` = price of the next copy when n L1-equivalents are
   * owned, NOT counting the item's `freeCopies` (pre-placed copies; see ShopItemDef).
   */
  shop: ShopItemDef[];
  /** In firing order (§0.4). */
  unlocks: UnlockDef[];
  /** Tutorial tips in order (§2.4 phase 7 "tutorial actions"). */
  tutorial: TutorialDef[];
  stalls: StallDef[];
  board: BoardDef;
  economy: EconomyDef;
}

export interface MaterialDef {
  id: Mat;
  version: Version;
}

export interface UnitDef {
  mat: Mat;
  n: Int;
}

export interface FamilyDef {
  id: FamId;
  kind: Kind;
  version: Version;
  /** Highest tier in `contentVersion` (a merge at this tier swaps with a "MAX" tag). */
  maxTier: Int;
  /** Version each tier arrives in (index t-1); its length is the tier count over all versions. */
  tierVersions: Version[];
  names: string[];
  /** Explicit integer value per tier (index t-1). */
  values: Int[];
  /** Material units in one T1 item. */
  units: UnitDef[];
  /** Heat a burned item of tier t gives (index t-1); empty when the family is not a fuel. */
  fuelHeat: Int[];
  mergeAll: boolean;
  quickMerge: 'all' | 'discovered';
  /** Sell protection in `contentVersion`: 'all' (v0.1 Cork Popper), 'last' (v0.2 guns), 'none'. */
  protect: 'none' | 'last' | 'all';
}

export interface GunTierDef {
  tier: Int;
  version: Version;
  /** Payout per shot = floor((V(ammo) * multPermille + 500) / 1000). */
  multPermille: Int;
  fireTicks: Int;
}

export interface CellDef {
  x: Int;
  y: Int;
}

export interface DepositDef {
  id: OreId;
  version: Version;
  name: string;
  fam: FamId;
  mat: Mat;
  hardMilli: Int;
  shop: ShopId;
  /** Pre-placed deposit (the starter Lead Vein) and the unlock effect that raises it. */
  preplaced: (CellDef & { effect: EffectId }) | null;
  protectLast: boolean;
}

export interface OddsDef {
  tier: Int;
  permille: Int;
}

export interface DepositRules {
  /** Spawn odds by deposit level (index L-1); each level sums to 1000. */
  oddsByLevel: OddsDef[][];
  maxSpawnsPerTick: Int;
  /** Damage carry cap = accCapHardness × hardMilli (R1). */
  accCapHardness: Int;
}

export interface MachineRules {
  maxLevel: Int;
  /** R2 work per tick by level (index L-1). */
  workPerTick: Int[];
  /** Cycle cost = cycle ticks at L1 × workPerL1Tick. */
  workPerL1Tick: Int;
  outCap: Int;
  maxCompletionsPerTick: Int;
}

export interface GhostReq {
  fam: FamId;
  tier: Int;
  n: Int;
}

export interface BuildingBase {
  type: BType;
  version: Version;
  name: string;
  /** Shop entry (price table); null = not sold. */
  shop: ShopId | null;
  /** First-build ghost requirements; empty = never a ghost. */
  ghost: GhostReq[];
  protectLast: boolean;
}

export interface ModeDef {
  id: string;
  version: Version;
  recipes: RecipeId[];
}

export interface DrillDef extends BuildingBase {
  hitTicks: Int[];
  powerMilli: Int[];
}

export interface CrateDef extends BuildingBase {
  capacity: Int[];
}

export interface ChuteDef extends BuildingBase {
  hopTicks: Int;
}

export interface ProcessorDef extends BuildingBase {
  cycleTicks: Int;
  /** Work per cycle = cycleTicks × workPerL1Tick. */
  cost: Int;
  inCap: Int;
  modes: ModeDef[];
}

export interface SmelterDef extends ProcessorDef {
  fuel: FamId;
  heatCap: Int[];
}

export interface MatcherDef extends BuildingBase {
  cycleTicks: Int;
  cost: Int;
  slotCap: Int;
  recipe: RecipeId;
  /** Slot families in fixed order (the recipe inputs). */
  slots: FamId[];
}

export interface PackerDef extends BuildingBase {
  mergeTicks: Int;
  mergeCost: Int;
  buffer: Int;
  /** R5 target cap by level (index L-1), before the family maximum is applied. */
  targetCapByLevel: Int[];
}

export interface GalleryDef extends BuildingBase {
  ammoQueue: Int;
  /** Booth gun cap by gallery level (v0.2). */
  gunCapByLevel: Int[];
  preplaced: CellDef & { gunTier: Int };
  backdrops: string[];
}

export interface GunsmithDef extends MatcherDef {
  tierCapByLevel: { enabled: boolean; caps: Int[] };
}

export interface DockDef extends BuildingBase {
  sellTicks: Int;
  sellCost: Int;
  queue: Int;
}

export interface BuildingDefs {
  drill: DrillDef;
  crate: CrateDef;
  chute: ChuteDef;
  caster: ProcessorDef;
  grinder: ProcessorDef;
  smelter: SmelterDef;
  press: ProcessorDef;
  loader: MatcherDef;
  packer: PackerDef;
  gallery: GalleryDef;
  gunsmith: GunsmithDef;
  dock: DockDef;
}

export interface RecipeDef {
  id: RecipeId;
  version: Version;
  inputs: FamId[];
  out: FamId;
  /** Highest tier the recipe processes (over all versions). */
  maxTier: Int;
  /** Heat needed per cycle by tier (index t-1); empty = no heat. */
  heatNeed: Int[];
  match: 'direct' | 'laggardClimb';
}

export interface ShopItemDef {
  id: ShopId;
  version: Version;
  kind: 'deposit' | 'building';
  /** OreId for deposits, BType for buildings. */
  ref: string;
  flat: boolean;
  /**
   * Pre-placed copies that are owned from the start, free, and excluded from n (GDD §6): 1 for the starter
   * Lead Vein and the Gallery, else 0. With only the starter vein owned, Lead Vein #2 costs prices[0].
   */
  freeCopies: Int;
  /**
   * Price of the next copy by n (index n) = L1-equivalents owned minus `freeCopies` (a level-L copy counts
   * 2^(L−1); machines count per type). A flat price has one entry. Past the end = sold out.
   */
  prices: Int[];
}

export type TriggerDef =
  | { type: 'start' }
  | { type: 'practiceEmpty' }
  | { type: 'itemCount'; fam: FamId; n: Int }
  | { type: 'firstMerge' }
  | { type: 'firstSale' }
  | { type: 'depositLevel'; ore: OreId; level: Int }
  | { type: 'made'; fam: FamId; minTier: Int }
  | { type: 'madeBy'; fam: FamId; building: BType }
  | { type: 'handMerges'; n: Int }
  | { type: 'built'; building: BType }
  | { type: 'expanded'; expansion: ExpansionId }
  | { type: 'fired'; fam: FamId; minTier: Int; source: 'any' | 'factory' }
  | { type: 'galleryBinding'; binding: 'starved' | 'gunLimited'; forTicks: Int }
  | { type: 'autoIncome'; perSec: Int }
  | { type: 'unlocked'; id: UnlockId }
  | { type: 'buildingLevel'; building: BType; level: Int }
  | { type: 'refused'; stall: StallId }
  | { type: 'mounted'; minTier: Int }
  | { type: 'holdHits'; n: Int };

export type UnlockTrigger = TriggerDef | { type: 'all'; of: TriggerDef[] };

/**
 * What the goal chip prices while an unlock is the current goal (M4a goalView): the next copy of a shop item
 * (its current price), an expansion (its cost), or an automated income target in $/s (no cash ETA).
 */
export type GoalTarget =
  | { type: 'shop'; id: ShopId }
  | { type: 'expansion'; id: ExpansionId }
  | { type: 'autoIncome'; perSec: Int };

export interface EffectDef {
  id: EffectId;
  /** The effect applies only when `contentVersion` is at least this. */
  version: Version;
}

export interface UnlockDef {
  id: UnlockId;
  version: Version;
  /**
   * Prerequisite (M4a "masks nest"): the unlock only fires after this one. Compiled from the table order:
   * the nearest earlier unlock with chips (the main line); toast/banner-only unlocks are side branches that
   * never block a later unlock. null for the first unlock.
   */
  after: UnlockId | null;
  trigger: UnlockTrigger;
  effects: EffectDef[];
  /** copy.json keys, shown in order in the goal chip. */
  chips: string[];
  /** What the chip prices while this unlock is the current goal; null = no price / ETA. */
  goal: GoalTarget | null;
  toast: string | null;
  banner: string | null;
  checklist: FamId[];
}

/** A tutorial tip: shown once `after` has fired, retired when `done` holds (UI in M7). */
export interface TutorialDef {
  id: TipId;
  version: Version;
  /** copy.json key of the hint text. */
  copy: string;
  after: UnlockId;
  done: UnlockTrigger;
}

export interface StallDef {
  id: StallId;
  version: Version;
  cls: 'error' | 'wait' | 'paused';
  graceTicks: Int;
}

export interface ExpansionDef {
  id: ExpansionId;
  version: Version;
  x0: Int;
  y0: Int;
  x1: Int;
  y1: Int;
  cost: Int;
}

export interface BoardDef {
  cols: Int;
  rows: Int;
  /** The first entry is the starting mask; the rest are bought in order. */
  expansions: ExpansionDef[];
}

export interface EconomyDef {
  shopVisiblePermille: Int;
  sellPermille: { counter: Int; crateSellAll: Int; dock: Int };
  buildRefundPermille: Int;
  buildUndoDepth: Int;
  coldOpen: { practiceRounds: Int; ammoTier: Int };
  /**
   * R3 manual mining. `sharedCooldown` (GDD D13 lever) makes taps share the hold cooldown with `bufferedTaps`
   * queued; false in v0.1 (§0 wins: a tap hits on pointerdown, taps capped at 12/s by the input layer).
   */
  manual: { tapDamageMilli: Int; holdRepeatTicks: Int; sharedCooldown: boolean; bufferedTaps: Int };
  mergeAllCooldownTicks: Int;
  income: { bucketTicks: Int; ringBuckets: Int; etaBuckets: Int };
  galleryReadout: {
    bucketTicks: Int;
    buckets: Int;
    starvedIdlePermille: Int;
    gunLimitedFullPermille: Int;
  };
  /** R17: a producer busy at least this share of the readout window is named as the bottleneck. */
  bottleneck: { busyPermille: Int };
  /**
   * R17 v2 best next step (lead decision L10, rules/advice.ts): value terms are soft-capped at the units/s the
   * automated-income target needs × targetMarginPermille / 1000; stage, Loader-family and output weights.
   */
  advice: { targetMarginPermille: Int; stageWeight: Int; familyWeight: Int; outputWeight: Int };
  offline: {
    version: Version;
    efficiencyPermille: Int;
    minAwaySec: Int;
    baseCapSec: Int;
    avgWindowSec: Int;
    medianMinuteBuckets: Int;
    earlyCapGoalMult: Int;
    earlyCapUntil: UnlockId;
  };
  supplyDrops: {
    version: Version;
    everySec: Int;
    maxPerReturn: Int;
    traySlots: Int;
    pendingMax: Int;
    weights: { ore: Int; part: Int; ammo: Int; gun: Int };
    ammoTiersBelowBestFired: Int;
  };
  bench: {
    version: Version;
    tapPower: { costs: Int[]; damageMilli: Int[] };
    holdSpeed: { costs: Int[]; holdRepeatTicks: Int[] };
    nightShift: { costs: Int[]; capSecPerLevel: Int };
  };
}

/** True when an entry of version `v` ships in `defs.contentVersion`. */
export function isActive(defs: SimDefs, v: Version): boolean {
  return VERSIONS.indexOf(v) <= VERSIONS.indexOf(defs.contentVersion);
}
